'use client'

import { Swiper, SwiperSlide } from 'swiper/react'
import { SwiperOptions } from 'swiper/types'
import { A11y, Keyboard, FreeMode, Mousewheel } from 'swiper/modules'
import { SanityImageAssetDocument } from 'next-sanity'
import { SanityImage } from '@otomoro/components/shared/SanityImage/SanityImage'
import { useState } from 'react'

import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/free-mode'
import 'swiper/css/keyboard'
import clsx from "clsx"

type Props = SwiperOptions & {
	images: SanityImageAssetDocument[]
	className?: string
	autoHeight?: boolean
	imageClassName?: string
	figureClassName?: string
	slideClassName?: string
	download?: boolean
	hasOffsetAfter?: boolean
	hasOffsetBefore?: boolean
	unevenLayout?: boolean
	slidePerView?: number | 'auto'
}

export function ImageSlider({
	images,
	className,
	autoHeight = false,
	imageClassName,
	slideClassName,
	download = false,
	figureClassName,
	hasOffsetAfter = false,
	hasOffsetBefore = false,
	loop = true,
	centeredSlides = true,
	unevenLayout = false,
	slidePerView = 'auto',
	...swiperOptions
}: Props) {
	const [isInit, setIsInit] = useState(false)
	return (
		<div className={clsx("", isInit ? 'is-init' : undefined, className)}>
			<Swiper
				modules={[A11y, Keyboard, FreeMode, Mousewheel]}
				className=""
				freeMode={true}
				mousewheel={true}
				grabCursor={true}
				slidesPerView={slidePerView}
				spaceBetween={16}
				slideToClickedSlide
				slidesOffsetAfter={hasOffsetAfter ? 24 : 0}
				slidesOffsetBefore={hasOffsetBefore ? 24 : 0}
				loop={loop}
				centeredSlides={centeredSlides}
				onSwiper={() => setIsInit(true)}
				keyboard={{
					enabled: true,
					onlyInViewport: true,
				}}
				a11y={{
					enabled: true,
					prevSlideMessage: 'Previous slide',
					nextSlideMessage: 'Next slide',
				}}
				{...swiperOptions}
				breakpoints={{
					768: {
						slidesOffsetAfter: hasOffsetAfter ? 32 : 0,
						slidesOffsetBefore: hasOffsetBefore ? 32 : 0,
						spaceBetween: 32,
					},
					1024: {
						slidesOffsetAfter: hasOffsetAfter ? 96 : 0,
						slidesOffsetBefore: hasOffsetBefore ? 96 : 0,
						spaceBetween: 32,
					},
				}}
			>
				{images.map((image: SanityImageAssetDocument, idx: number) => (
					<SwiperSlide
						key={idx}
						className={clsx(
							"",
							unevenLayout && idx % 2 !== 0 ? 'is-odd' : undefined,
							slideClassName
						)}
					>
						<SanityImage
							image={image.image}
							className={clsx("", imageClassName)}
							alt={image.image.alt || ''}
							caption={image.image.caption || ''}
							figureClass={clsx("pointer-events-none", figureClassName)}
							revealCaption={true}
							download={download}
							sizes='(max-width: 768px) 80vw, (max-width: 1200px) 50vw, 50vw'
							priority={true}
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	)
}
